/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { Observable, of, Subject } from 'rxjs';
import { VenuesService } from '@app/settings/venues/venues.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Venue } from '@app/settings/venues/venue.model';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { VenuesFormService } from '@app/settings/venues/venues-form.service';
import { Logger } from '@app/core';

const log = new Logger('VenueSelectComponent');

@Component({
  selector: 'app-venue-select',
  templateUrl: './venue-select.component.html',
  styleUrls: ['./venue-select.component.scss'],
})
export class VenueSelectComponent implements OnInit {
  @Input() parentFormGroup: FormGroup;
  @Input() distance: object = null;
  @Input() passFormControlName = 'id';
  @Input() onlyActive = true;
  @Output() change = new EventEmitter();
  @Output() select = new EventEmitter();
  loader: boolean;
  apiQuery: ApiQuery = new ApiQuery();
  data: Observable<Venue[]>;
  selected: Venue; // използва се за directions за момента
  input$ = new Subject<string>();
  lastResponse: any = null;
  @Input() isInModal = true;

  constructor(private venueService: VenuesService, private venueFormService: VenuesFormService) {}

  ngOnInit() {
    const preSelectedVenueId = this.parentFormGroup.get(this.passFormControlName);

    if (preSelectedVenueId && preSelectedVenueId.value > 0) {
      this.loadById(preSelectedVenueId.value);
    } else if (preSelectedVenueId) {
      const preselectedVenueIdSubscriber = preSelectedVenueId.valueChanges.subscribe((venueId: any) => {
        if (venueId) {
          this.loadById(venueId);
          preselectedVenueIdSubscriber.unsubscribe();
        }
      });
    }

    this.venueSearch();
  }

  loadVenues(keyword?: string) {
    this.loader = true;

    this.venueService
      .index(
        this.apiQuery.addIncludes('city.country').addFilters({
          name: keyword,
          active: true,
        })
      )
      .subscribe((response: PaginatedResponse) => {
        this.data = of(response.data);
        this.loader = false;
      });
  }

  venueSearch() {
    const query = new ApiQuery().addIncludes('city.country');

    if (this.onlyActive) {
      query.addFilter('active', true);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.venueService.index(query.addFilter('name', term)).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.loader = false))
          );
        })
      )
      .subscribe((venues: PaginatedResponse) => {
        this.data = of(venues.data);
        this.lastResponse = venues;
        this.loader = false;
      });
  }

  loadById(id: number) {
    this.loader = true;
    this.venueService.show(id).subscribe((venue: Venue) => {
      this.data = of([venue]);
      this.handleChange(venue);
      this.loader = false;
    });
  }

  /**
   * Пази избраното venue в променливата this.selected
   * @param venue Избраното venue от търсенето
   */
  handleChange(venue: Venue) {
    this.selected = venue;
    this.change.emit(venue);
    this.select.emit(venue);
  }

  add(searchTerm: string) {
    this.venueFormService.open(new Venue().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadById(response.id);
        this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  setData(data: Venue) {
    if (data) {
      this.data = of([data]);
    } else {
      this.data = of([]);
    }
  }
}
